import { WORKFLOWS_NS } from '../utils/i18n-namespaces';
import { useState } from 'react';
import { sendToast } from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';

import { ActionFile } from '../components';
import {
  uploadFile,
  uploadFilesSequentially,
  progressServerConfig,
} from '../utils';
import { parseCsv } from '../utils/csv-parser';
import {
  extractCsvGroupMeta,
  type ImportedGroupMeta,
} from '../utils/csv-grouping/csv-group-meta';
import { tableStore } from '../stores/table-store';
import {
  useProStudioCtxSelector,
  sendProStudioEvent,
} from '../xstate/app.xstate';
import {
  captureAnalyticsEvent,
  classifyAnalyticsError,
  solutionAnalyticsProps,
  MAX_ANALYTICS_ERROR_MESSAGE_LENGTH,
} from '../utils/analytics';

export const useUploadCSV = () => {
  const [uploadingFiles, setUploadingFiles] = useState<ActionFile[]>([]);
  // PSTUDIO-4122: the import's report is raised HERE rather than in the store or
  // the machine, because this is the only one of the three with translations.
  const [t] = useTranslation();

  // Flattened to primitives (not a nested object) so useProStudioCtxSelector's
  // shallow-equal check actually short-circuits on unrelated context changes,
  // instead of a fresh object literal comparing unequal on every transition.
  const {
    solutionVersionId,
    customer_id: customerId,
    project_id: projectId,
    project_name: projectName,
    solution_name: solutionName,
  } = useProStudioCtxSelector((ctx) => ({
    solutionVersionId: ctx.draftVersionSolutionData?.id ?? null,
    ...solutionAnalyticsProps(ctx),
  }));

  const removePlaceholder = (id: string) => {
    setUploadingFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const uploadOne = (file: File, index: number): Promise<void> => {
    // `uploadOne` closes over this render's customerId/projectId/etc. and
    // solutionVersionId, so a failure is attributed to the solution the
    // upload was ISSUED for even if the user switches solution mid-upload —
    // not the one on screen when the error callback fires. That matches how
    // reload-nodes.ts attributes via eventSolutionVersionId rather than live
    // context, and is deliberate, not a stale-closure bug.
    //
    // Stable key for this in-flight placeholder, matched on exactly instead of
    // the previous `name.startsWith` (PSTUDIO-3173).
    const id = `${file.name}-${index}-${Date.now()}`;

    setUploadingFiles((prev) => [
      ...prev,
      { id, name: `${file.name} (uploading: 0%)`, path: '', loading: true },
    ]);

    return new Promise<void>((resolve, reject) => {
      if (!solutionVersionId) {
        removePlaceholder(id);
        reject(new Error('Solution version ID is required'));
        return;
      }

      uploadFile({
        url: `${import.meta.env.FE_ADMIN_URL}/rest/import-nodes/${solutionVersionId}`,
        file,
        type: 'text/csv',
        fieldName: 'file',
        onChange: () => {
          removePlaceholder(id);
          resolve();
        },
        // CSV import is fail-fast: reject so uploadFilesSequentially stops and
        // reloadNodes below is skipped (no partial import is committed).
        error: (error) => {
          console.error(error);
          removePlaceholder(id);

          // The user is served FIRST, and the report comes after. This
          // callback runs from `request.onload`, outside the `new Promise`
          // executor above, so anything that throws while building the
          // analytics payload settles nothing: `isUploading` stays true and
          // the CSV modal suppresses Esc, outside clicks and both buttons
          // (`csv-upload-modal.tsx:50-52,72,82`) — a WCAG 2.1.2 keyboard trap
          // whose only exit is a page reload. Same call as
          // `prostudio.xstate.ts` (commit 7de8dc4801): reporting must not be
          // able to break the path that shows the user what went wrong.
          sendProStudioEvent({
            type: 'showError',
            error,
          });

          // `String(error)` rather than a bare `.slice`: this parameter is
          // typed `string`, but its producer assigns
          // `JSON.parse(request.response).message` — an `any` — to it
          // (`utils/upload-file.ts:97-107`), so a backend body whose `message`
          // is an object reaches here as one. `.slice` on it throws a
          // `TypeError`, and `captureAnalyticsEvent`'s own `try/catch` cannot
          // see it because it is evaluated in the argument list.
          const rawMessage = String(error);
          const errorMessage = rawMessage.slice(
            0,
            MAX_ANALYTICS_ERROR_MESSAGE_LENGTH
          );

          captureAnalyticsEvent('csv_upload_failed', {
            customer_id: customerId,
            project_id: projectId,
            project_name: projectName,
            solution_name: solutionName,
            solution_version_id: solutionVersionId,
            filename: file.name,
            file_count: 1,
            failure_stage: 'upload',
            error_kind: classifyAnalyticsError(errorMessage),
            error_message: errorMessage,
          });

          // Rejected with the FULL message, not the truncated copy: the 250
          // bound exists for what leaves the browser, not for what stays in it.
          reject(new Error(rawMessage));
        },
        progress: (current, loaded, total) => {
          progressServerConfig(current, loaded, total, (p) => {
            setUploadingFiles((prev) =>
              prev.map((uploadingFile) =>
                uploadingFile.id === id
                  ? {
                      ...uploadingFile,
                      name: `${file.name} (uploading: ${p}%)`,
                    }
                  : uploadingFile
              )
            );
          });
        },
      });
    });
  };

  /**
   * PSTUDIO-4122: graft the imported grouping and tell the user what actually
   * happened.
   *
   * Three outcomes, three different words, because the old single success toast
   * was raised from the machine on the strength of `landed` alone — a flag
   * computed BEFORE the graft runs, so it could not know whether the grouping
   * survived. An import whose groups were dropped reported "CSV successfully
   * imported" and nothing else.
   *
   * Only reached when the reload landed: a reload that failed or was dropped is
   * still reported by the machine's `onErrorActions`, and raising anything here
   * would double-toast it.
   */
  const reportImportOutcome = (
    metas: ImportedGroupMeta[],
    uploadedFor: string
  ) => {
    const outcome = metas.length
      ? tableStore.applyImportedGrouping(metas, uploadedFor)
      : ({ status: 'nothing-to-do' } as const);

    if (outcome.status === 'version-changed') {
      // The nodes are in; the grouping is not, and re-importing into the version
      // the user is now on is the only way to get it. Saying so beats a success
      // toast beside a table with no groups in it.
      sendToast({
        header: String(
          t(`${WORKFLOWS_NS}:csv_import.toast.grouping_lost_header`)
        ),
        text: String(t(`${WORKFLOWS_NS}:csv_import.toast.grouping_lost_text`)),
        type: 'warning',
        toastId: 'csv-import-grouping-lost',
      });
      return;
    }

    if (outcome.status === 'applied' && outcome.skipped.length) {
      // A group is skipped when its members cannot be matched back by Node
      // Number — typically a CSV exported from a DIFFERENT solution, whose
      // numbers were renumbered on import.
      sendToast({
        header: String(
          t(`${WORKFLOWS_NS}:csv_import.toast.groups_skipped_header`)
        ),
        text: String(
          t(`${WORKFLOWS_NS}:csv_import.toast.groups_skipped_text`, {
            count: outcome.skipped.length,
          })
        ),
        type: 'warning',
        toastId: 'csv-import-grouping-skipped',
      });
      return;
    }

    sendToast({
      text: String(t(`${WORKFLOWS_NS}:csv_import.toast.success`)),
      type: 'success',
      toastId: 'csv-import-success',
    });
  };

  const handleUpload = async (files: File[], onSuccess: () => void) => {
    if (!files?.length) return;
    if (!solutionVersionId) return;

    const fileArr = Array.from(files);

    // PSTUDIO-4122: pin the STORE identity at the moment the CSV is issued.
    // `solutionVersionId` above is `draftVersionSolutionData.id` — a UUID the
    // REST `/rest/import-nodes/` endpoint wants. `tableStore.solutionVersionId`
    // is `${customerId}-${botName}-${draftVersion}`, the PK
    // `tenancy_solution_versions_ps_by_pk` uses. `reload-nodes.ts` already
    // names them as different tables; comparing the UUID against the store
    // would refuse every successful same-version import as `version-changed`.
    const uploadedFor = tableStore.solutionVersionId;

    // PSTUDIO-3443: Parse group metadata from each file BEFORE upload so we
    // have the grouping info available for post-reload reconstruction.
    // parseCsv is worker-based + async; a parse failure must NEVER block the
    // base-node import — grouping is best-effort.
    const metas = (
      await Promise.all(
        fileArr.map(async (f) => {
          try {
            return extractCsvGroupMeta(await parseCsv(await f.text()));
          } catch (e) {
            console.error('[useUploadCSV] group-meta parse failed:', e);
            return [];
          }
        })
      )
    ).flat();

    await uploadFilesSequentially(fileArr, uploadOne);

    // PSTUDIO-3443: The reloadNodes cb fires in the `finally` block of the
    // reload-nodes invoke, which runs AFTER processVersionData completes, so the
    // base nodes are in tableStore before applyImportedGrouping matches them by
    // Node Number.
    //
    // PSTUDIO-4103: `finally` runs even when PSTUDIO-3953's identity guard
    // DROPPED the payload as belonging to a version the store has left, so the
    // graft is gated on the `applied` flag the invoke now passes. Node numbers
    // collide across solutions and applyImportedGrouping writes structurally
    // (updateRow with saveImmediately, insertRowAbove), so running it against a
    // foreign table grafts one solution's group headers into another — and
    // persists them. `onSuccess` stays UNCONDITIONAL: it is the CSV modal's
    // handleSuccess, which closes the modal and clears `isUploading`, so
    // skipping it would leave the modal on a spinner forever.
    sendProStudioEvent({
      type: 'reloadNodes',
      payload: {
        solutionVersionId,
        filenames: fileArr.map((f) => f.name),
        cb: (applied) => {
          if (applied) reportImportOutcome(metas, uploadedFor);
          onSuccess?.();
        },
      },
    });
  };

  return {
    uploadingFiles,
    handleUpload,
  };
};
